import React, { Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { AsgFwContext } from '../shared/src/contexts';
import { AsgAppContext, AsgDictionaryContext } from './src/contexts';
import { Provider } from 'react-redux';

// WeakMap to hold React roots keyed by container DOM elements
const rootsMap = new WeakMap();

export const createReactRoot = (element) => {
    let root = rootsMap.get(element);
    if (!root) {
        root = createRoot(element);
        rootsMap.set(element, root);
    }
    return root;
};

// Lazy-loaded components (remap named exports to default)
const StatusBarWrapLazy = lazy(() =>
    import('./src/containers/StatusBarWrap')
        .then((mod) => ({ default: mod.StatusBarWrap }))
);
const StatusBarSubheaderLazy = lazy(() =>
    import('./src/containers/StatusBarSubheader')
        .then((mod) => ({ default: mod.StatusBarSubheader }))
);

// Wrapper components to include contexts and providers
const StatusBarWrapLoader = ({ statusBarData, store }) => (
    <AsgFwContext.Provider value={AsgFW}>
        <AsgAppContext.Provider value={AsgApp}>
            <AsgDictionaryContext.Provider value={Dictionary}>
                <Provider store={store}>
                    <Suspense fallback={null}>
                        <StatusBarWrapLazy statusBarData={statusBarData} />
                    </Suspense>
                </Provider>
            </AsgDictionaryContext.Provider>
        </AsgAppContext.Provider>
    </AsgFwContext.Provider>
);

const StatusBarSubheaderLoader = ({ statusBarData, store }) => (
    <AsgFwContext.Provider value={AsgFW}>
        <AsgAppContext.Provider value={AsgApp}>
            <AsgDictionaryContext.Provider value={Dictionary}>
                <Provider store={store}>
                    <Suspense fallback={null}>
                        <StatusBarSubheaderLazy statusBarData={statusBarData} />
                    </Suspense>
                </Provider>
            </AsgDictionaryContext.Provider>
        </AsgAppContext.Provider>
    </AsgFwContext.Provider>
);

// Render function for status bar using root cache and lazy loading
export const renderStatusBarComponent = (state) => {
    if (!state?.showStatusBar) {return;}

    const container = document.getElementById('status-bar');
    if (!container) {
        console.warn('Status bar container element not found');
        return;
    }

    const { store } = AsgSharedComponents.state;

    const root = createReactRoot(container);

    try {
        root.render(<StatusBarWrapLoader statusBarData={state} store={store} />);
    } catch (e) {
        Tools.error('Status bar render failed:', e);
    }
};

// Render function for subheader status bar using root cache and lazy loading
export const renderStatusBarSubheader = (state) => {
    const container = document.getElementsByClassName('subheader-desktop-statusbar')[0];
    if (!container) {
        console.warn('Subheader status bar container element not found');
        return;
    }

    const { store } = AsgSharedComponents.state;

    const root = createReactRoot(container);

    try {
        root.render(<StatusBarSubheaderLoader statusBarData={state} store={store} />);
    } catch (e) {
        Tools.error('Subheader status bar render failed:', e);
    }
};

export const renderStatusBar = (state) => {
    try {
        renderStatusBarComponent(state);
    } catch (e) {
        Tools.error('Status bar render failed', e);
    }
};
